<!-- SearchEDocuments -->
<script lang="ts" setup>
    import SearchBase from './SearchBase.vue';
    import { ISearchModeProps } from './SearchUtils';
    import { ref, reactive, onMounted, watch } from 'vue';
    import { ISearchBaseInputs } from '../../lib/Models/SearchModel';
    import * as choiceApi from '../../lib/ChoiceApi'
    import { DatabaseIdTypes } from '../../Enums/ChoiceEnums';
    import { ISearchDocumentResult, ISearchDocumentRequest } from '../../lib/Api/DocumentManager';
    import { isValidDate } from '../../lib/DateUtils';
    import { toInt } from '../../lib/Convert/String';
    import { ToPrettyNumber } from '../../lib/StringFormat'
    import { EDocumentsSearchModeType } from './SearchEnums';
    import PrettyFileSize from '../PrettyFileSize.vue';
    import MessageBadge from '../Messages/MessageBadge.vue';
    import { DisplayStyle } from '../../Enums/UiEnums';
    import { getFileExt } from '../../lib/FileUtils'
    import { ModalStyles, popAlert } from '../../lib/Notification';

    type TResult = ISearchDocumentResult;

    const props = withDefaults(defineProps<ISearchModeProps & {
        idType: DatabaseIdTypes,
        id: number|string,
        defaultDaysSinceCreated?: number,
        maxAllowedFileSizeInBytes?: number,
        allowedFileTypeExtensions?:string[]
    }>(), {
        label: "eDocuments Search",
        searchMode: EDocumentsSearchModeType.daysSinceCreate,
        defaultDaysSinceCreated: 30
    });

    const emit = defineEmits<{
        "onChange": [selectedItem?: TResult],
        "update:searchMode": [newMode: string]
    }>();

    const searchColumns = ref(
        [
            { title: "File Name", element: "fileName" },
            { title: "Created On", element: "createDate" },
            { title: "Size", element: "fileSizeInBytes" },
            { title: "Cabinet", element: "cabinetName" },
            { title: "Drawer", element: "drawerName" },
            { title: "Folder", element: "folderName" },
        ],        
    );

    const compData = reactive<{
        searchMode?: EDocumentsSearchModeType
    }>({});

    const searchResults = ref<TResult[]>([]);
    const internalDefaultSearchText = ref<string>("");    
    const processing = ref<boolean>(false);
    const displayColumn = ref<string>("fileName");
    const baseComp = ref();


    onMounted(()=>{
        compData.searchMode = props.searchMode as EDocumentsSearchModeType;

        internalDefaultSearchText.value = props.defaultSearchText ?? "";
    });

    watch(() => props.defaultSearchText, (newVal, _oldVal) =>{
        internalDefaultSearchText.value = newVal ?? "";
    });

    function onChange(selectedItem?: TResult) {
        emit("onChange", selectedItem);
        if (selectedItem)
        {
            internalDefaultSearchText.value = (selectedItem as any)[displayColumn.value]?.toString()
        }
    }

    const searchModeOptions = [EDocumentsSearchModeType.daysSinceCreate, EDocumentsSearchModeType.fileName, EDocumentsSearchModeType.createDate, EDocumentsSearchModeType.cabinetName, EDocumentsSearchModeType.drawerName, EDocumentsSearchModeType.folderName ];

    async function performSearchBase(inputs: ISearchBaseInputs)  {
        // Use the default days as the default days back to search
        if (EDocumentsSearchModeType.daysSinceCreate === compData.searchMode)
        {
            const days = toInt(inputs.searchText) ?? props.defaultDaysSinceCreated ?? 30;
            internalDefaultSearchText.value = days.toString();
            inputs.searchText = internalDefaultSearchText.value;
        } 

        processing.value = true;
        const payload: ISearchDocumentRequest = {
            fileName: (EDocumentsSearchModeType.fileName === compData.searchMode) ? inputs.searchText : undefined,
            createDate: (EDocumentsSearchModeType.createDate === compData.searchMode && isValidDate(inputs.searchText)) ? inputs.searchText : undefined,
            cabinetName: (EDocumentsSearchModeType.cabinetName === compData.searchMode) ? inputs.searchText : undefined,
            drawerName: (EDocumentsSearchModeType.drawerName === compData.searchMode) ? inputs.searchText : undefined,
            folderName: (EDocumentsSearchModeType.folderName === compData.searchMode) ? inputs.searchText : undefined,
            daysSinceCreate: (EDocumentsSearchModeType.daysSinceCreate === compData.searchMode) ? toInt(inputs.searchText) : undefined,
        }

        searchResults.value = [];
        const results = await choiceApi.documentManager.searchDocuments(props.idType, props.id, payload);
        if (results)
        {
            searchResults.value = results;
        }
    }

    function updateSearchMode(newMode: string)
    {
        compData.searchMode = newMode as EDocumentsSearchModeType;
        emit("update:searchMode", newMode);

        // Trigger a search
        baseComp.value?.onSearch();
    }

    function allowSelectedItemCheck(selectedItem: any): boolean
    {
        // Don't allow selections of nothing
        if (!selectedItem)
            return false;

        // Block non-allowed file types
        if (!isAllowedFileExtension(selectedItem?.fileName))
        {
            const msg = "The specified file type is not one of the allowed file types: " + props.allowedFileTypeExtensions?.join(", ");
            popAlert(msg, "Invalid File", ModalStyles.danger);
            return false;
        }
        return true;
    }

    function isAllowedFileExtension(fileName: string): boolean
    {
        // If there isn't a list of allowed types, allow anything
        if (!props.allowedFileTypeExtensions)
            return true;

        const ext = getFileExt(fileName);
        for(const allowedExt of props.allowedFileTypeExtensions)
        {
            if (-1 !== allowedExt.indexOf('.'))
            {
                alert(`Invalid allowed extension type: ${allowedExt} -- dot not allowed for extension`);
            }

            if (allowedExt.toLowerCase() === ext.toLowerCase())
                return true;
        }
        return false;
    }
</script>

<template>
    <SearchBase
        ref="baseComp"
        :displayColumn="displayColumn"
        :columns="searchColumns" 
        :searchResults="searchResults"
        @performSearchBase="performSearchBase"
        :performAllowSelectedItemCheck="allowSelectedItemCheck"
        @onChange="onChange"
        
        :searchModes="searchModeOptions"
        :searchMode="compData.searchMode"
        @update:searchMode="updateSearchMode"

        :label="props.label"
        :placeholder="props.placeholder"  
        :defaultSearchText="internalDefaultSearchText"
        :processing="processing"
        :enableAutoSearch="props.enableAutoSearch"
        :showClearSearch="props.showClearSearch"
        :resetFocus="props.resetFocus">
        <template #cell="{ row, col }">
            <template v-if="'fileSizeInBytes' === col.element">
                <PrettyFileSize :bytes="row[col.element]" />
                <MessageBadge v-if="row[col.element] > (props.maxAllowedFileSizeInBytes ?? 0)" 
                    :displayStyle="DisplayStyle.Danger"
                    :title="`Max file size: ${ToPrettyNumber(props.maxAllowedFileSizeInBytes)} bytes`">
                    Too big
                </MessageBadge>
            </template>
            <template v-else-if="'fileName' === col.element && props.allowedFileTypeExtensions?.length">
                {{ row[col.element] }}
                <div v-if="!isAllowedFileExtension(row[col.element])">
                    <MessageBadge 
                        :displayStyle="DisplayStyle.Danger"
                        title="This file type is not allowed">
                        Invalid File Type
                    </MessageBadge>
                </div>
            </template>
            <template v-else>
                {{ row[col.element] }}
            </template>
        </template>
    </SearchBase>
</template>